/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState, useRef, useReducer, useCallback } from 'react';
import { useLocation } from 'react-router';
import {
  faCircleXmark,
  /* faEye, */ faGear,
  faHistory,
  faUserFriends,
  faRotate,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IonContent, IonPage, IonButton, IonText /* , useIonRouter */ } from '@ionic/react';
import '../../styles/profile/Profile.scss';
import pizzaPic from '../../assets/foodfight_pizza.png';
import profilePic from '../../assets/profiles/foodfight_profile_baseball.png';
import { PageContext /* TakerNotificationContext */ } from '../../contexts/AppContexts';
import { AppContext } from '../../global/AppContext';
import MatchmakingContext from '../../global/MatchmakingContext';
import { acceptFight, getFinishedBetsPage, getLiveBets } from '../../global/request/bet';
import { fetchUserLimitPrizeOffers, redeemPrizeOffer } from '../../global/request/prizeOffers';
import useProfile from '../../global/useProfile';
import useRestaurants from '../../global/useRestaurants';
import { AuthContext } from '../authentication/AuthContext';
import FriendsList from '../friendProfile/FriendsList';
import CouponCard from './CouponCard';
import FightHistory from './FightHistory';
import LiveFights from './LiveFights';
import RewardsDisplay from './RewardsDisplay';
import SummaryPopup from './SummaryPopup';

const HISTORY_SLICE_LENGTH = 6;
const COUPONS_PER_PAGE = 5;
const COMPONENT_ROUTE_NAME = '/app/profile';

const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_REFRESHING: 'SET_REFRESHING',
  SET_LIVE_FIGHTS: 'SET_LIVE_FIGHTS',
  SET_ACTIVE_COUPONS: 'SET_ACTIVE_COUPONS',
  SET_ACTIVE_COUPON_PAGE: 'SET_ACTIVE_COUPON_PAGE',
  SET_HAS_MORE_ACTIVE_COUPONS: 'SET_HAS_MORE_ACTIVE_COUPONS',
  SET_TOTAL_ACTIVE_COUPONS: 'SET_TOTAL_ACTIVE_COUPONS',
  REMOVE_ACTIVE_COUPON: 'REMOVE_ACTIVE_COUPON',
  SET_PENDING_COUPONS: 'SET_PENDING_COUPONS',
  SET_REDEEMED_COUPONS: 'SET_REDEEMED_COUPONS',
  SET_REDEMPTION_DATES: 'SET_REDEMPTION_DATES',
  REDEEM_COUPON: 'REDEEM_COUPON',
  SET_REDEEM_COUPON_PAGE: 'SET_REDEEM_COUPON_PAGE',
  SET_HAS_MORE_REDEEM_COUPONS: 'SET_HAS_MORE_REDEEM_COUPONS',
  SET_TOTAL_REDEEM_COUPONS: 'SET_TOTAL_REDEEM_COUPONS',
  SET_SHOW_FRIENDS: 'SET_SHOW_FRIENDS',
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  SET_FRIENDS_LIST_CHANGED: 'SET_FRIENDS_LIST_CHANGED',
  SET_TRIGGER_FOLLOW_BUTTONS_REFRESH: 'SET_TRIGGER_FOLLOW_BUTTONS_REFRESH',
  SET_TRIGGER_FIGHT_HISTORY_REFRESH: 'SET_TRIGGER_FIGHT_HISTORY_REFRESH',
  RESET_FRIENDS_LIST_CHANGED: 'RESET_FRIENDS_LIST_CHANGED',
};

const initialState = {
  // Loading states
  loading: true,
  refreshing: false,

  // Fight data
  liveFights: [],

  // Active coupon data
  activeCoupons: [],
  couponActivePage: 0,
  hasMoreActiveCoupons: true,
  totalActiveCoupons: 0,

  // Redeemed coupon data
  couponRedeemPage: 0,
  redeemedCoupons: [], // IDs only
  redeemedCouponsFull: [], // Full objects
  redemptionDates: {},
  hasMoreRedeemCoupons: true,
  totalRedeemCoupons: 0,

  // Pending data
  pendingCoupons: [],

  // UI state
  showFriends: false,
  activeTab: 'Game Logs',

  // Refresh triggers
  hasFriendsListChanged: true,
  triggerFollowButtonsRefresh: false,
  triggerFightHistoryRefresh: false,
};

function profileReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };

    case ACTIONS.SET_REFRESHING:
      return { ...state, refreshing: action.payload };

    case ACTIONS.SET_LIVE_FIGHTS:
      return { ...state, liveFights: action.payload };

    case ACTIONS.SET_ACTIVE_COUPONS: {
      const newCoupons = action.payload || [];
      const existingCoupons = state.activeCoupons || [];

      const mergedCoupons = [...existingCoupons.filter(c => !newCoupons.some(nc => nc.id === c.id)), ...newCoupons];

      return {
        ...state,
        activeCoupons: mergedCoupons,
      };
    }

    case ACTIONS.SET_ACTIVE_COUPON_PAGE:
      return { ...state, couponActivePage: action.payload };

    case ACTIONS.SET_HAS_MORE_ACTIVE_COUPONS:
      return { ...state, hasMoreActiveCoupons: action.payload };

    case ACTIONS.SET_TOTAL_ACTIVE_COUPONS:
      return { ...state, totalActiveCoupons: action.payload };

    case ACTIONS.REMOVE_ACTIVE_COUPON:
      return {
        ...state,
        activeCoupons: state.activeCoupons.filter(coupon => coupon.id !== action.payload),
        totalActiveCoupons: state.totalActiveCoupons - 1,
      };

    case ACTIONS.SET_PENDING_COUPONS:
      return { ...state, pendingCoupons: action.payload };

    case ACTIONS.SET_REDEEMED_COUPONS: {
      const newIds = action.payload.ids || [];
      const newFull = action.payload.full || [];
      const newDates = action.payload.dates || {};

      const mergedIds = [...new Set([...state.redeemedCoupons, ...newIds])];
      const mergedFull = [...state.redeemedCouponsFull.filter(c => !newIds.includes(c.id)), ...newFull];
      const mergedDates = {
        ...state.redemptionDates,
        ...newDates,
      };

      return {
        ...state,
        redeemedCoupons: mergedIds,
        redeemedCouponsFull: mergedFull,
        redemptionDates: mergedDates,
      };
    }

    case ACTIONS.SET_REDEMPTION_DATES:
      return { ...state, redemptionDates: action.payload };

    case ACTIONS.REDEEM_COUPON: {
      const { coupon, currentDate } = action.payload;
      const redeemedCoupon = { ...coupon, status: 'redeemed', updatedAt: currentDate };

      return {
        ...state,
        coupons: state.activeCoupons.filter(c => c.id !== coupon.id),
        redeemedCoupons: [...state.redeemedCoupons, coupon.id],
        redeemedCouponsFull: [...state.redeemedCouponsFull, redeemedCoupon],
        redemptionDates: {
          ...state.redemptionDates,
          [coupon.id]: currentDate,
        },
      };
    }

    case ACTIONS.SET_REDEEM_COUPON_PAGE:
      return { ...state, couponRedeemPage: action.payload };

    case ACTIONS.SET_HAS_MORE_REDEEM_COUPONS:
      return { ...state, hasMoreRedeemCoupons: action.payload };

    case ACTIONS.SET_TOTAL_REDEEM_COUPONS:
      return { ...state, totalRedeemCoupons: action.payload };

    case ACTIONS.SET_SHOW_FRIENDS:
      return { ...state, showFriends: action.payload };

    case ACTIONS.SET_ACTIVE_TAB:
      return { ...state, activeTab: action.payload };

    case ACTIONS.SET_FRIENDS_LIST_CHANGED:
      return { ...state, hasFriendsListChanged: action.payload };

    case ACTIONS.SET_TRIGGER_FOLLOW_BUTTONS_REFRESH:
      return { ...state, triggerFollowButtonsRefresh: !state.triggerFollowButtonsRefresh };

    case ACTIONS.SET_TRIGGER_FIGHT_HISTORY_REFRESH:
      return { ...state, triggerFightHistoryRefresh: !state.triggerFightHistoryRefresh };

    case ACTIONS.RESET_FRIENDS_LIST_CHANGED:
      return { ...state, hasFriendsListChanged: false };

    default:
      return state;
  }
}

function formatAddress(fullAddress) {
  const match = fullAddress.match(/^[^,]+,\s*([^,]+),\s*([A-Z]{2})\s*(\d{5})/);
  if (!match) return fullAddress; // fallback
  const [, city, state, zip] = match;
  return `${city}, ${state} ${zip}`;
}

function Profile() {
  // const router = useIonRouter(); // Uncomment if routing needed in future

  const location = useLocation();
  const authCtx = useContext(AuthContext);
  const pageCtx = useContext(PageContext);
  const appCtx = useContext(AppContext);
  const { state: matchmakingState } = useContext(MatchmakingContext);
  const [, restaurants] = useRestaurants();
  const contentRef = useRef(null);
  const historyRef = useRef(null);
  // const {
  //   /* openTakerNotification, closeTakerNotification */
  // } = useContext(TakerNotificationContext);
  // // These are not used yet but kept for future notification handling

  const [reload, profileData] = useProfile();
  const [loadedFights /* setLoadedFights */] = useState([]);
  // setLoadedFights unused now, may be useful later
  const [state, dispatch] = useReducer(profileReducer, initialState);

  // const openModal = () => setShowModal(true);  // Modal handlers not used yet
  // const closeModal = () => setShowModal(false);

  const bearerToken = authCtx.tokens.idToken;

  useEffect(() => {
    (async () => {
      const fights = await getLiveBets(authCtx.tokens.idToken);
      dispatch({ type: ACTIONS.SET_LIVE_FIGHTS, payload: fights });
    })();
  }, [authCtx.tokens.idToken]);

  useEffect(() => {
    dispatch({ type: ACTIONS.SET_FRIENDS_LIST_CHANGED, payload: true });
  }, [matchmakingState.triggerGetFriendsList]);

  useEffect(() => {
    if (location.pathname !== COMPONENT_ROUTE_NAME || !state.hasFriendsListChanged) return;
    reload();
    dispatch({ type: ACTIONS.SET_TRIGGER_FOLLOW_BUTTONS_REFRESH });
    dispatch({ type: ACTIONS.RESET_FRIENDS_LIST_CHANGED });
  }, [location.pathname, state.hasFriendsListChanged, reload]);

  const fetchActiveCoupons = useCallback(
    async (page = 0) => {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      try {
        const token = authCtx.tokens.idToken;

        if (!profileData?.id || !token) return;

        const offset = page * COUPONS_PER_PAGE;
        const status = 'active';
        const paginatedResponse = await fetchUserLimitPrizeOffers(token, COUPONS_PER_PAGE, offset, status);

        if (!paginatedResponse?.success || !Array.isArray(paginatedResponse.offers)) {
          console.warn('Invalid offers response:', paginatedResponse);
          return;
        }

        const activeCoupons = paginatedResponse.offers.filter(c =>
          ['active', 'confirmed', 'available', 'complete'].includes(c.status),
        );

        dispatch({ type: ACTIONS.SET_ACTIVE_COUPONS, payload: activeCoupons });

        const total = paginatedResponse.pagination?.total_active_count ?? 0;
        dispatch({ type: ACTIONS.SET_TOTAL_ACTIVE_COUPONS, payload: total });
        dispatch({
          type: ACTIONS.SET_HAS_MORE_ACTIVE_COUPONS,
          payload: offset + paginatedResponse.offers.length < total,
        });
        dispatch({ type: ACTIONS.SET_ACTIVE_COUPON_PAGE, payload: page });
      } catch (err) {
        console.error('Failed to fetch coupons:', err);
      } finally {
        dispatch({ type: ACTIONS.SET_LOADING, payload: false });
      }
    },
    [authCtx.tokens.idToken, profileData?.id],
  );

  const fetchRedeemCoupons = useCallback(
    async (page = 0) => {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      try {
        const token = authCtx.tokens.idToken;

        if (!profileData?.id || !token) return;

        const offset = page * COUPONS_PER_PAGE;
        const status = 'redeemed';
        const paginatedResponse = await fetchUserLimitPrizeOffers(token, COUPONS_PER_PAGE, offset, status);
        if (!paginatedResponse?.success || !Array.isArray(paginatedResponse.offers)) {
          console.warn('Invalid offers response:', paginatedResponse);
          return;
        }

        const redeemed = paginatedResponse.offers.filter(c => c.status === 'redeemed');

        const redemptionMap = {};
        redeemed.forEach(c => {
          redemptionMap[c.id] = c.updatedAt || c.redeemedAt || new Date().toISOString();
        });

        dispatch({
          type: ACTIONS.SET_REDEEMED_COUPONS,
          payload: {
            ids: redeemed.map(c => c.id),
            full: redeemed,
            dates: redemptionMap,
          },
        });

        const total = paginatedResponse.pagination?.total_redeemed_count ?? 0;
        dispatch({ type: ACTIONS.SET_TOTAL_REDEEM_COUPONS, payload: total });
        dispatch({
          type: ACTIONS.SET_HAS_MORE_REDEEM_COUPONS,
          payload: offset + paginatedResponse.offers.length < total,
        });
        dispatch({ type: ACTIONS.SET_REDEEM_COUPON_PAGE, payload: page });
      } catch (err) {
        console.error('Failed to fetch coupons:', err);
      } finally {
        dispatch({ type: ACTIONS.SET_LOADING, payload: false });
      }
    },
    [authCtx.tokens.idToken, profileData?.id],
  );

  useEffect(() => {
    if (authCtx.tokens.idToken) {
      fetchActiveCoupons(0); // load first page by default
      fetchRedeemCoupons(0);
      // no pending coupons
    } else {
      console.warn('No auth token, skipping coupon fetch.');
    }
  }, [authCtx.tokens.idToken, fetchActiveCoupons, fetchRedeemCoupons]);

  const handleRedeem = async coupon => {
    try {
      const response = await redeemPrizeOffer(bearerToken, coupon);
      const data = response; // no .json()
      if (data?.success) {
        const currentDate = new Date().toISOString();
        console.log('redeemed coupon', coupon);
        dispatch({ type: ACTIONS.REMOVE_ACTIVE_COUPON, payload: coupon.id });
        dispatch({
          type: ACTIONS.REDEEM_COUPON,
          payload: { coupon, currentDate },
        });
        dispatch({ type: ACTIONS.SET_TOTAL_REDEEM_COUPONS, payload: state.totalRedeemCoupons + 1 });
      }
    } catch (err) {
      console.error('Error redeeming coupon:', err);
    }
  };

  const scrollToHistory = () => {
    if (contentRef.current && historyRef.current) {
      const { offsetTop } = historyRef.current;
      contentRef.current.scrollToPoint(0, offsetTop - 20, 500);
    }
  };

  const goToPrizeTab = () => {
    dispatch({ type: ACTIONS.SET_ACTIVE_TAB, payload: 'Prizes' });
    if (contentRef.current) {
      contentRef.current.scrollToPoint(0, 0, 0);
    }
  };

  const refreshPendingFights = async () => {
    try {
      dispatch({ type: ACTIONS.SET_REFRESHING, payload: true });
      const updatedLiveFights = await getLiveBets(authCtx.tokens.idToken);
      dispatch({ type: ACTIONS.SET_LIVE_FIGHTS, payload: updatedLiveFights });
      dispatch({ type: ACTIONS.SET_TRIGGER_FIGHT_HISTORY_REFRESH });
    } catch (error) {
      console.error('Error refreshing pending fights:', error);
    } finally {
      dispatch({ type: ACTIONS.SET_REFRESHING, payload: false });
    }
  };

  const handleAcceptFight = async (fightId, paymentId, deliveryAddress, takerItems, deliveryInstructions, lat, lng) => {
    try {
      await acceptFight(bearerToken, fightId, paymentId, deliveryAddress, takerItems, deliveryInstructions, lat, lng);
    } catch (error) {
      console.error('Error refreshing fights', error);
    }
    refreshPendingFights();
  };

  return (
    <IonPage>
      <IonContent ref={contentRef} className='profile-page'>
        <div className='profile-page-wrapper'>
          <div className='profile-header-section'>
            <div className='profile-info-block'>
              <div className='profile-image-wrapper'>
                <img src={profilePic} className='profile-image' alt='Profile' />
              </div>
              <div className='profile-name-wrapper'>
                <span className='username-and-friends'>
                  <p className='pn-name'>{profileData?.name}</p>
                  {profileData?.username && <p className='pn-username'>| @{profileData?.username}</p>}
                </span>
                <div className='profile-button-wrapper'>
                  <button
                    type='button'
                    className='friends-btn'
                    onClick={() => dispatch({ type: ACTIONS.SET_SHOW_FRIENDS, payload: !state.showFriends })}>
                    <FontAwesomeIcon icon={faUserFriends} /> {profileData?.friends.length}{' '}
                    {profileData?.friends?.length < 2 ? 'Friend' : 'Friends'}
                  </button>
                  <button
                    type='button'
                    className='friends-btn'
                    onClick={() => pageCtx.modals.settings.current.present()}>
                    <FontAwesomeIcon icon={faGear} />
                    Settings
                  </button>
                </div>
              </div>
            </div>
          </div>

          {state.showFriends && (
            <SummaryPopup>
              <div className='friends-section'>
                <div className='friends-container'>
                  <div className='friends-header'>
                    <p style={{ visibility: 'hidden' }}>.</p>
                    <p className='friend-title'>Friends</p>
                    <button
                      type='button'
                      className='close-btn'
                      onClick={() => dispatch({ type: ACTIONS.SET_SHOW_FRIENDS, payload: false })}>
                      <FontAwesomeIcon icon={faCircleXmark} />
                    </button>
                  </div>
                  <FriendsList
                    tab='friends'
                    friendsIds={profileData?.friends || []}
                    triggerFollowButtonsRefresh={state.triggerFollowButtonsRefresh}
                    selfFriendsIds={profileData?.friends || []}
                  />
                </div>
              </div>
            </SummaryPopup>
          )}

          <RewardsDisplay />

          <div className='toggle-view-container'>
            <div className='toggle-view-wrapper'>
              <div className='tab-buttons'>
                <button
                  type='button'
                  className={state.activeTab === 'Game Logs' ? 'active' : ''}
                  onClick={() => dispatch({ type: ACTIONS.SET_ACTIVE_TAB, payload: 'Game Logs' })}>
                  Game Logs
                </button>
                <button
                  type='button'
                  className={state.activeTab === 'Prizes' ? 'active' : ''}
                  onClick={() => dispatch({ type: ACTIONS.SET_ACTIVE_TAB, payload: 'Prizes' })}>
                  Prizes
                </button>
              </div>
            </div>

            <div className='tab-content'>
              {state.activeTab === 'Game Logs' ? (
                <div className='logs-content'>
                  <div className='history-btn-wrapper'>
                    <button
                      type='button'
                      className='history-btn'
                      onClick={refreshPendingFights}
                      style={{ marginLeft: '8px' }}>
                      <FontAwesomeIcon icon={faRotate} />
                    </button>
                    Refresh
                    <button type='button' className='history-btn' onClick={scrollToHistory}>
                      <FontAwesomeIcon icon={faHistory} />
                    </button>
                    History
                  </div>
                  <LiveFights
                    tab='live'
                    liveFights={state.liveFights}
                    routeName={COMPONENT_ROUTE_NAME}
                    refreshing={state.refreshing}
                    onAcceptFight={handleAcceptFight}
                  />
                  <div className='history-section'>
                    <p ref={historyRef} className='live-component-title' style={{ fontWeight: '600' }}>
                      HISTORY
                    </p>
                    <FightHistory
                      loadedFights={loadedFights}
                      fetchFights={page => getFinishedBetsPage(authCtx.tokens.idToken, page)}
                      record={{ wins: profileData?.wins, losses: profileData?.losses, ties: profileData?.ties }}
                      profileId={profileData?.id}
                      profileName={profileData?.name}
                      routeName={COMPONENT_ROUTE_NAME}
                      refreshTrigger={state.triggerFightHistoryRefresh}
                      prizePage={goToPrizeTab}
                      bearerToken={bearerToken}
                    />
                  </div>
                </div>
              ) : (
                <div className='prizes-wrapper'>
                  {!(state.activeCoupons.length > 0 || state.redeemedCouponsFull.length > 0) && (
                    <IonText className='no-coupons-text'>It&rsquo;s quite empty here... Start a FoodFight!</IonText>
                  )}
                  {
                    // pending coupons are currently set to not display
                    state.pendingCoupons.length > 0 && (
                      <>
                        <h3 style={{ display: 'none' }}>Pending Coupons</h3>
                        {state.pendingCoupons.map(coupon => (
                          <CouponCard
                            key={coupon.id}
                            coupon={coupon}
                            isRedeemed={false}
                            status={coupon.status}
                            onRedeem={handleRedeem}
                          />
                        ))}
                      </>
                    )
                  }
                  {state.activeCoupons.length > 0 && (
                    <>
                      <h3>Available Coupons</h3>
                      {/* <pre>{JSON.stringify(coupons, null, 2)}</pre> */}
                      {state.activeCoupons.map(coupon => {
                        const matchedRestaurant = restaurants.find(r => String(r.id) === String(coupon.restaurant_id));
                        const normalizedCoupon = {
                          expire: coupon.expiration ? new Date(coupon.expiration * 1000) : new Date('2025-06-12'),

                          placeIcon: matchedRestaurant?.icon || '/default-icon.png',
                          placeName:
                            coupon.restaurantName ||
                            coupon.placeName ||
                            matchedRestaurant?.name ||
                            'Unknown Restaurant',
                          placeLocation: formatAddress(matchedRestaurant?.address || 'United States'),

                          itemName:
                            coupon.items?.[0]?.name ||
                            coupon.itemName ||
                            coupon.details?.items?.[0]?.name ||
                            'Unknown Item',
                          itemAmount:
                            coupon.items?.[0]?.quantity ??
                            coupon.itemAmount ??
                            coupon.details?.items?.[0]?.quantity ??
                            1,
                          itemCustomization:
                            coupon.items?.[0]?.itemCustomization ||
                            coupon.itemCustomization ||
                            coupon.details?.items?.[0]?.itemCustomization ||
                            {},
                          matchID: coupon.id || null,
                          status: coupon.status || 'unknown',
                          warning: {
                            image: pizzaPic,
                            message: 'ONLY REDEEM THIS COUPON IF YOU ARE THE VENUE STAFF/ BARTENDER!',
                            title: 'Caution!',
                          },
                        };

                        return (
                          <CouponCard
                            key={coupon.id}
                            coupon={normalizedCoupon}
                            onRedeem={() => handleRedeem(coupon)}
                            redeemed={state.redeemedCoupons.includes(coupon.id)}
                            redemptionDate={state.redemptionDates[coupon.id] || null}
                            bearerToken={authCtx.tokens.idToken}
                          />
                        );
                      })}
                      {state.hasMoreActiveCoupons && (
                        <div className='loadMoreBtn-container'>
                          <IonButton
                            className='loadMoreBtn'
                            size='small'
                            onClick={() => fetchActiveCoupons(state.couponActivePage + 1)}>
                            Load {Math.min(COUPONS_PER_PAGE, state.totalActiveCoupons - state.activeCoupons.length)}{' '}
                            More ({state.totalActiveCoupons} total)
                          </IonButton>
                        </div>
                      )}
                    </>
                  )}
                  {state.redeemedCouponsFull.length > 0 && (
                    <>
                      <h3>Redeemed Coupons</h3>
                      {state.redeemedCouponsFull
                        .filter(
                          coupon =>
                            coupon.restaurant_id && (coupon.items?.[0]?.name || coupon.itemName || coupon.details),
                        )
                        .map(coupon => {
                          if (!coupon) return null;
                          // Normalize fields like you do for available coupons
                          const matchedRestaurant = restaurants.find(
                            r => String(r.id) === String(coupon.restaurant_id),
                          );
                          const normalizedCoupon = {
                            expire: coupon.expiration ? new Date(coupon.expiration * 1000) : new Date('2025-06-12'),

                            placeIcon: matchedRestaurant?.icon || '/default-icon.png',
                            placeName:
                              coupon.restaurantName ||
                              coupon.placeName ||
                              matchedRestaurant?.name ||
                              'Unknown Restaurant',
                            placeLocation: formatAddress(matchedRestaurant?.address || 'United States'),

                            itemName:
                              coupon.items?.[0]?.name ||
                              coupon.itemName ||
                              coupon.details?.items?.[0]?.name ||
                              'Unknown Item',
                            itemAmount:
                              coupon.items?.[0]?.quantity ??
                              coupon.itemAmount ??
                              coupon.details?.items?.[0]?.quantity ??
                              1,
                            itemCustomization:
                              coupon.items?.[0]?.itemCustomization ||
                              coupon.itemCustomization ||
                              coupon.details?.items?.[0]?.itemCustomization ||
                              {},
                            matchID: coupon.id || null,
                            status: coupon.status || 'unknown',
                          };
                          return (
                            <CouponCard
                              key={coupon.id}
                              coupon={normalizedCoupon}
                              onRedeem={() => handleRedeem(coupon)}
                              redeemed={state.redeemedCoupons.includes(coupon.id)}
                              redemptionDate={state.redemptionDates[coupon.id] || null}
                              bearerToken={authCtx.tokens.idToken}
                            />
                          );
                        })}
                      {state.hasMoreRedeemCoupons && (
                        <div className='loadMoreBtn-container'>
                          <IonButton
                            className='loadMoreBtn'
                            size='small'
                            onClick={() => fetchRedeemCoupons(state.couponRedeemPage + 1)}>
                            Load {Math.min(COUPONS_PER_PAGE, state.totalRedeemCoupons - state.redeemedCoupons.length)}{' '}
                            More ({state.totalRedeemCoupons} total)
                          </IonButton>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}

export default Profile;
